import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import FabBar from "@/design/components/FabBar";
import MasterListVirtualized from "@/design/components/MasterListVirtualized";
import { disableSearch, enableSearch } from "@/store/reducers/ui";
import useSearch from "@/utils/hooks/useSearch";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viCity, viPlus } from "@sippy-platform/valkyrie";

import { useMediaQuery, useTheme } from "@mui/material";
import Button from "@mui/material/Button";
import { useHotkey } from "@tanstack/react-hotkeys";

import AddPbx from "./AddPbx";

function PbxList() {
  const { context } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const matchesMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const auth = useSelector((state) => state.auth);
  const pbxStore = useSelector((state) => state.backstage.pbxes);
  const searchValue = useSelector((state) => state.ui.searchValue);
  const progressBar = useSelector((state) => state.ui.progressBar);

  const [manageDialog, setManageDialog] = useState(false);

  useHotkey("Alt+N", () => setManageDialog(true));

  // Create pbx dialog
  async function closeDialog() {
    setManageDialog(false);
    await Promise.resolve(Api.getPbxes());
  }

  // Search every time pbxList or searchQuery changes
  const searchResults = useSearch(pbxStore.list, searchValue, ["name", "context"]);

  useEffect(() => {
    toggleLoadingState(true);
    Promise.resolve(Api.getPbxes()).finally(toggleLoadingState);
  }, []);

  // Open first item or new if the viewport is large enough
  useEffect(() => {
    if (!context && matchesMdUp && pbxStore.meta.lastFetched) {
      if (pbxStore.list.length >= 1) {
        navigate(`${pbxStore.list[0].context}`);
      } else {
        navigate("new");
      }
    }
  }, [context, matchesMdUp, navigate, pbxStore.list, pbxStore.meta.lastFetched]);

  useEffect(() => {
    dispatch(enableSearch());
  }, [dispatch]);
  useEffect(() => () => dispatch(disableSearch()), [dispatch]);

  return (
    <>
      {auth.accessLevel <= 1 && manageDialog && (
        <AddPbx open={manageDialog} closeDialog={closeDialog} navigateOnCreate />
      )}
      <MasterListVirtualized
        fabBar
        title="PBXes"
        aria-label="PBX list"
        id="domain-list"
        list={searchResults}
        listItemProps={{
          primary: (x) => x.name,
          secondary: (x) => x.context,
          to: (x) => `../${x.context}`,
          selected: (x) => x.context === context,
        }}
        hideContentLoader={pbxStore.meta.count >= 1 && searchResults.length >= 1}
        contentLoaderProps={{
          count: pbxStore.meta.count,
          loading: progressBar,
          noItems: "You don't have any PBXes yet",
          noResults: "No PBXes in your current query",
          fetching: "Getting your PBXes",
          icon: viCity,
        }}
      />
      {auth.accessLevel <= 1 && (
        <FabBar>
          <Button variant="contained" onClick={() => setManageDialog(true)} startIcon={<Valkyrie icon={viPlus} />}>
            Add
          </Button>
        </FabBar>
      )}
    </>
  );
}

export default PbxList;
